import { FC, ReactNode, useLayoutEffect, useRef } from "react";
import classNames from "classnames";

import { Alert } from "../../Alert/Alert";
import {
	ContentBlock as IContentBlock,
	CustomContentBlockData,
	DualChartContentBlockData,
	ImageGridContentBlockData,
	StatGridContentBlockData,
	KpiCardsContentBlockData,
	OpeningTextContentBlockData,
	ResponsiveImageContentBlockData,
	TextContentBlockData,
	TextWithImageContentBlockData,
	TextWithChartContentBlockData,
	ImageContentBlockData,
	HeaderContentBlockData,
	PullQuoteContentBlockData,
} from "../ContentBuilder.type";
import { CustomBlock } from "../CustomBlock/CustomBlock";
import { KpiBlock } from "../KpiBlock/KpiBlock";
import { ImageBlock } from "../ImageBlock/ImageBlock";
import { ImageGridBlock } from "../ImageGridBlock/ImageGridBlock";
import { StatGridBlock } from "../StatGridBlock/StatGridBlock";
import { TextBlock } from "../TextBlock/TextBlock";
import { TextWithImageBlock } from "../TextWithImageBlock/TextWithImageBlock";
import { ResponsiveImageBlock } from "../ResponsiveImageBlock/ResponsiveImageBlock";
import { DualChartBlock } from "../DualChartBlock/DualChartBlock";
import { TextWithChartBlock } from "../TextWithChartBlock/TextWithChartBlock";
import { useMarginaliaContext } from "../MarginaliaContext/MarginaliaContext";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { UiBreakpoints } from "../../../config/ui/breakpoints";
import { Reveal } from "../../Reveal/Reveal";

import { ContentBlockProps } from "./ContentBlock.type";
import { HeaderBlock } from "../HeaderBlock/HeaderBlock";
import { PullQuoteBlock } from "../PullQuoteBlock/PullQuoteBlock";
import { DividerBlock } from "../DividerBlock/DividerBlock";

// Blocks that reveal their own children in sequence. They opt out of the block-level fade so
// that a single fade over the whole block does not run on top of the cascade inside it.
const SELF_REVEALING_TYPES = ["stat-grid"];

// Full-width chart blocks get extra breathing room above and below so they read as a
// standalone figure rather than as part of the surrounding text flow.
const FULL_WIDTH_CHART_TYPES = ["custom", "dual-chart"];

const ContentBlock: FC<ContentBlockProps> = ({ block, id, index }) => {
	// h3 headings: slightly tighter below than above, so they attach visually to the content they introduce.
	// The negative bottom margin subtracts from the next block's collapsed top margin (24/32px → 16/20px).
	const isH3Header = block.type === "header" && (block.data as HeaderContentBlockData).type === "h3";
	const isFullWidthChart = FULL_WIDTH_CHART_TYPES.includes(block.type);
	const blockSpacing = isH3Header
		? "mt-6 md:mt-8 -mb-2 md:-mb-3"
		: isFullWidthChart
			? "my-10 md:my-14"
			: "my-6 md:my-8";
	const blockClassName = classNames(blockSpacing, block.className);
	const { registerBlock, unregisterBlock, isInline } = useMarginaliaContext();
	const wrapperRef = useRef<HTMLDivElement>(null);
	const { width } = useWindowSize();

	// On phones (image stacked above text), inline marginalia belong between the image
	// and the text of left-image blocks. From `sm` up the image floats/sits beside the
	// text, so the marginalia keep their usual place below the block.
	const isImageLeftBlock =
		block.type === "text-image" &&
		["image-left", "image-left-column"].includes((block.data as TextWithImageContentBlockData).type);
	const inlineMarginaliaInsideBlock = isInline && Boolean(block.marginalia) && isImageLeftBlock && width < UiBreakpoints.sm;

	const inlineMarginalia = isInline && block.marginalia && (() => {
		const MarginaliaComponent = block.marginalia!.component;
		return <MarginaliaComponent {...(block.marginalia!.props ?? {})} />;
	})();

	useLayoutEffect(() => {
		if (!block.marginalia) return;
		registerBlock(block.id, wrapperRef.current);
		return () => unregisterBlock(block.id);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	let content: ReactNode;

	switch (block.type) {
		case "kpi-cards":
			content = <KpiBlock data={block.data as KpiCardsContentBlockData} />;
			break;

		case "opening-text": {
			const data = block.data as OpeningTextContentBlockData;
			const paragraphs = Array.isArray(data.text) ? data.text : [data.text];
			content = paragraphs.map((p, i) => (
				<p key={i} className="text-lg leading-7 mb-4 last:mb-0 text-justify" dangerouslySetInnerHTML={{ __html: p }} />
			));
			break;
		}

		case "text":
			content = <TextBlock id={id} data={block.data as TextContentBlockData} />;
			break;

		case "header":
			content = <HeaderBlock id={id} data={block.data as HeaderContentBlockData} chartLabel={block.chartLabel} />;
			break;

		case "divider":
			content = <DividerBlock id={id} />;
			break;

		case "pull-quote":
			content = <PullQuoteBlock id={id} data={block.data as PullQuoteContentBlockData} />;
			break;

		case "text-image":
			content = (
				<TextWithImageBlock
					id={id}
					data={block.data as TextWithImageContentBlockData}
					marginalia={inlineMarginaliaInsideBlock ? inlineMarginalia : undefined}
				/>
			);
			break;

		case "responsive-image":
			content = <ResponsiveImageBlock id={id} data={block.data as ResponsiveImageContentBlockData} chartLabel={block.chartLabel} />;
			break;

		case "image-grid":
			content = <ImageGridBlock id={id} data={block.data as ImageGridContentBlockData} />;
			break;

		case "stat-grid":
			content = <StatGridBlock id={id} data={block.data as StatGridContentBlockData} />;
			break;

		case "image":
			content = <ImageBlock id={id} data={block.data as ImageContentBlockData} />;
			break;

		case "alert": {
			const { title, message, ...rest } = block.data;
			content = (
				<Alert
					{...rest}
					{...(typeof title === "string" && { title: title as string })}
					{...(typeof message === "string" && { message: message as string })}
				/>
			);
			break;
		}

		case "text-chart":
			content = <TextWithChartBlock id={id} data={block.data as TextWithChartContentBlockData} chartLabel={block.chartLabel} />;
			break;

		case "dual-chart":
			content = <DualChartBlock id={id} data={block.data as DualChartContentBlockData} chartLabel={block.chartLabel} />;
			break;

		case "custom":
			content = <CustomBlock id={id} data={block.data as CustomContentBlockData} chartLabel={block.chartLabel} />;
			break;

		default:
			console.error(`Unknown content block type: ${(block as IContentBlock)?.type}`);
			return null;
	}

	const body = (
		<>
			{content}
			{inlineMarginalia && !inlineMarginaliaInsideBlock && <div className="mt-6">{inlineMarginalia}</div>}
		</>
	);

	return (
		<div ref={wrapperRef} className={blockClassName}>
			{SELF_REVEALING_TYPES.includes(block.type) ? body : <Reveal index={index}>{body}</Reveal>}
		</div>
	);
};

export { ContentBlock };
